/**
 * The pool preview a resolved invite code lands on. Presentation only: no API
 * call, no phase state. Everything renders from `model`.
 *
 * No start date, no Commissioner person row, no preamble, no capacity count,
 * no under-way line. None of those fields exist on `POST /pool/join-preview`
 * (see the plan's `## 0`). Do not invent a placeholder for any of them.
 */
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { PoolCardContent } from "./PoolCardContent";
import {
  contestTypeLabel,
  leagueShortLabel,
  type typeLeagueAbbreviations,
} from "./poolsListState";

export type typeJoinPoolPreviewModel = {
  poolId: string;
  name: string;
  leagueKey: string;
  contestType: string;
  activeMemberCount: number;
  rulesSummary: string;
};

export const JoinPoolPreviewSkeleton = (): ReactNode => (
  <div data-testid="join-preview-skeleton" className="flex flex-col gap-4">
    <Skeleton shape="card" label="Loading the pool" />
    <Skeleton shape="title" width="w-3/5" />
    <Skeleton shape="text" width="w-4/5" />
    <Skeleton shape="card" />
  </div>
);

export type typeJoinPoolPreviewProps = {
  model: typeJoinPoolPreviewModel;
  /** Reserved for ISSUE-481's invite path. Renders an inviter line when set. */
  inviterName?: string | undefined;
  /**
   * ISSUE-481: `/invite/get` returns server-written display labels, not the raw
   * keys `/pool/join-preview` returns. When set, the label is rendered verbatim
   * and the local helper is not called, honoring the rule that the client
   * renders a server-written label rather than re-deriving one.
   */
  leagueLabelOverride?: string | undefined;
  contestLabelOverride?: string | undefined;
  /**
   * leagueKey -> abbreviation, from POST /league-catalog/get (ISSUE-770).
   * Read only when `leagueLabelOverride` is not set.
   */
  leagueAbbreviations?: typeLeagueAbbreviations | undefined;
};

export const JoinPoolPreview = (props: typeJoinPoolPreviewProps): ReactNode => {
  const { model } = props;

  return (
    <div data-testid="join-preview" className="flex flex-col gap-4">
      <PoolCardContent
        heading
        name={model.name}
        contestType={
          model.contestType !== ""
            ? model.contestType
            : (props.contestLabelOverride ?? "")
        }
        facts={[
          props.leagueLabelOverride ??
            leagueShortLabel(model.leagueKey, props.leagueAbbreviations),
          props.contestLabelOverride ?? contestTypeLabel(model.contestType),
        ]}
        byline={
          props.inviterName === undefined
            ? undefined
            : `${props.inviterName} invited you.`
        }
      />
      <div className="flex flex-col gap-2">
        <h4 className="text-h2 text-ink">The rules</h4>
        <div className="rounded-md border border-line bg-surface p-4">
          <p className="text-body text-ink">{model.rulesSummary}</p>
        </div>
        <p className="text-caption text-ink-secondary">
          You agree to these rules by joining the contest.
        </p>
      </div>
    </div>
  );
};
